import { nextTick } from 'vue'
import { defineRouter } from '#q-app/wrappers'
import {
  createRouter,
  createMemoryHistory,
  createWebHistory,
  createWebHashHistory,
} from 'vue-router'
import routes from 'src/router/routes'
import { handleNavigationError } from 'src/boot/version-control'
import { posthog } from 'src/boot/posthog'

/*
 * If not building with SSR mode, you can
 * directly export the Router instantiation;
 *
 * The function below can be async too; either use
 * async/await or return a Promise which resolves
 * with the Router instance.
 */

// Create a variable to hold the router instance
let router

export default defineRouter(function (/* { store, ssrContext } */) {
  const createHistory = process.env.SERVER
    ? createMemoryHistory
    : process.env.VUE_ROUTER_MODE === 'history'
      ? createWebHistory
      : createWebHashHistory

  router = createRouter({
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      return false
    },
    routes,

    // Leave this as is and make changes in quasar.conf.js instead!
    // quasar.conf.js -> build -> vueRouterMode
    // quasar.conf.js -> build -> publicPath
    history: createHistory(process.env.VUE_ROUTER_BASE),
  })

  // Catch chunk load errors on every failed navigation (first and retries); registered here so we always have the router instance
  router.onError((error) => {
    handleNavigationError(error)
    throw error
  })

  // Track page navigation with time spent
  let pageEnterTime = Date.now()

  router.beforeEach((to, from) => {
    if (!from?.path || !from?.name) return
    if (!isPageChange(to, from)) return
    const timeOnPage = Date.now() - pageEnterTime
    const fromPageId = getPageId(from)
    posthog?.capture('$pageleave', {
      path: fromPageId,
      name: from.name,
      time_on_page_ms: timeOnPage,
      time_on_page_seconds: Math.round(timeOnPage / 1000),
    })
  })
  router.afterEach(async (to, from) => {
    // Scroll layout container to top when path changes (scrollable element is .q-page-container, not window)
    if (isPageChange(to, from)) {
      await nextTick()
      document.querySelector('.q-page-container')?.scrollTo({ top: 0 })
    } else {
      return
    }

    const now = Date.now()
    const toPageId = getPageId(to)

    // Track page view for new page
    posthog?.capture('$pageview', {
      path: toPageId,
      name: to.name,
    })

    // Reset timer for new page
    pageEnterTime = now
  })

  return router
})

function getPageId(route) {
  return route.path
}

function isPageChange(to, from) {
  return to.path !== from.path
}

export { router }
